import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import { AccountBalance } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';

import Access from 'ui/Access';
import Dashboard from 'ui/dashboard/Dashboard';
import DashboardBox from 'ui/dashboard/Box';
import DashboardBoxContent from 'ui/dashboard/BoxContent';
import DashboardBoxActions from 'ui/dashboard/BoxActions';
import PaperForm from 'ui/form/PaperForm';
import ButtonsForm from 'ui/form/ButtonsForm';
import SectionForm from 'ui/form/SectionForm';
import Fields from 'ui/form/FieldsForm';
import { getCountries } from '../../../../utils/county-data';
import * as valid from '../../../../utils/validators';
import PaymentInfo from '../PaymentInfo';
import { getPaymentMethod, postPaymentMethod } from '../services';
import { withAppContext } from '../../../AppContext';

const BOX_TYPE = 'box.type';
const EDIT_TYPE = 'edit.type';
const SAVE_TYPE = 'save.type';

const TYPES = {
    [BOX_TYPE]: PaymentBox,
    [EDIT_TYPE]: EditForm,
};

const FORM_VALIDATORS = valid.rules([
    valid.notEmpty('city'),
    valid.notEmpty('address1'),
    valid.notEmpty('country'),
    valid.notEmpty('postal_code'),
    valid.notEmpty('first_name'),
    valid.notEmpty('last_name'),
]);

const validateForm = data => valid.validate(FORM_VALIDATORS, data);

const Payment = props => {
    const { app } = props;
    const [type, setType] = useState(BOX_TYPE);
    const [errors, setErrors] = useState({});
    const [showProgress, setShowProgress] = useState(false);
    const defaultPayment = {
        empty: true,
        first_name: '',
        last_name: '',
        address1: '',
        city: '',
        country: 'US',
        postal_code: '',
    };
    const [payment, setPayment] = useState(defaultPayment);
    const rersetErrors = () => setErrors({});

    useEffect(() => {
        getPaymentMethod().then(payment => {
            setPayment({
                ...defaultPayment,
                ...payment,
                empty: !payment,
            });
        });
    }, []);

    const validateRecurlyData = err => {
        let allErrors = err.fields.reduce((results, name) => {
            results[name] = 'invalid';
            return results;
        }, {});
        validateForm(payment).then(errors => {
            if (errors) {
                allErrors = Object.assign(allErrors, errors);
            }
            setErrors(allErrors);
        });
    };

    const onAction = type => event => {
        if (type === SAVE_TYPE) {
            event.preventDefault();
            setShowProgress(true);

            window.recurly.token(
                document.querySelector('#recurly_form'),
                (err, token) => {
                    if (err) {
                        validateRecurlyData(err);
                        setShowProgress(false);
                    } else {
                        rersetErrors();

                        postPaymentMethod(token.id).then(payment_method => {
                            setShowProgress(false);
                            setPayment({
                                ...defaultPayment,
                                ...payment_method,
                                empty: false,
                            });
                            setType(BOX_TYPE);
                            app.updateUserData();
                        });
                    }
                }
            );
        } else {
            setType(type);
        }
    };

    const onChange = (name, value) =>
        setPayment({ ...payment, ...{ [name]: value } });

    const Component = TYPES[type];
    return (
        <Component
            onAction={onAction}
            onChange={onChange}
            errors={errors}
            payment={payment}
            showProgress={showProgress}
            app={app}
        />
    );
};

export default withAppContext(Payment);

function PaymentBox({ onAction, payment }) {
    return (
        <Dashboard align="center">
            <DashboardBox>
                <DashboardBoxContent>
                    {payment.empty ? (
                        <Typography>No payment information of file</Typography>
                    ) : (
                        <PaymentInfo payment={payment} />
                    )}
                </DashboardBoxContent>
                <Access.Change
                    permission={Access.ACCOUNT_DETAILS_PAYMENT_METHOD}
                >
                    <DashboardBoxActions>
                        <Button onClick={onAction(EDIT_TYPE)}>
                            {payment.empty ? 'Add' : 'Edit'}
                        </Button>
                    </DashboardBoxActions>
                </Access.Change>
            </DashboardBox>
        </Dashboard>
    );
}

function EditForm({ onAction, onChange, payment, errors, showProgress, app }) {
    const changeFactory = name => value => onChange(name, value);

    useEffect(() => {
        window.recurly.configure({
            publicKey: app.currentUser.settings.recurly_key,
            required: ['number', 'month', 'year', 'cvv'],
        });
    });

    return (
        <PaperForm title="Payment information">
            <SectionForm icon={AccountBalance}>
                <form id="recurly_form">
                    <Fields.Recurly
                        label="Card number"
                        recurlyName="number"
                        error={errors.number}
                    />

                    <Fields.OneLine>
                        <Fields.Recurly
                            label="MM"
                            recurlyName="month"
                            error={errors.month}
                        />
                        <Fields.Recurly
                            label="YYYY"
                            recurlyName="year"
                            error={errors.year}
                        />
                        <Fields.Recurly
                            label="CVV"
                            recurlyName="cvv"
                            error={errors.cvv}
                        />
                    </Fields.OneLine>
                    <Fields.OneLine>
                        <Fields.Text
                            label="First name"
                            inputProps={{
                                'data-recurly': 'first_name',
                            }}
                            error={errors.first_name}
                            value={payment.first_name}
                            onChange={changeFactory('first_name')}
                        />
                        <Fields.Text
                            label="Last name"
                            inputProps={{
                                'data-recurly': 'last_name',
                            }}
                            error={errors.last_name}
                            value={payment.last_name}
                            onChange={changeFactory('last_name')}
                        />
                    </Fields.OneLine>
                    <Fields.Select
                        label="Country"
                        error={errors.country}
                        value={payment.country}
                        inputProps={{
                            'data-recurly': 'country',
                        }}
                        onChange={changeFactory('country')}
                        options={getCountries()}
                    />
                    <Fields.Text
                        label="City"
                        inputProps={{
                            'data-recurly': 'city',
                        }}
                        error={errors.city}
                        value={payment.city}
                        onChange={changeFactory('city')}
                    />
                    <Fields.Text
                        label="Street"
                        inputProps={{
                            'data-recurly': 'address1',
                        }}
                        error={errors.address1}
                        value={payment.address1}
                        onChange={changeFactory('address1')}
                    />
                    <Fields.Text
                        label="ZIP / postal code"
                        inputProps={{
                            'data-recurly': 'postal_code',
                        }}
                        error={errors.postal_code}
                        value={payment.postal_code}
                        onChange={changeFactory('postal_code')}
                    />
                    <ButtonsForm>
                        <ButtonsForm.BackButton
                            label="Cancel"
                            onClick={onAction(BOX_TYPE)}
                        />
                        <ButtonsForm.NextButton
                            label="Save"
                            onClick={onAction(SAVE_TYPE)}
                            loading={showProgress}
                        />
                    </ButtonsForm>
                </form>
            </SectionForm>
        </PaperForm>
    );
}
